/**
 * iter53 — Widget conversationnel IA pour prise de RDV public.
 * iter102 (2026-02) — Refonte UX mobile + mode embed iframe.
 * iter103 (2026-02) — Architecture chatbot car-main.ch appliquée à Léa :
 *   - Mode `?embed=1&host=1` : masque le header interne (fix doublon sans
 *     toucher au wrapper car-main.ch). ☎️ devient un mini-lien discret.
 *   - Barre de saisie alignée sur ChatWidget.jsx (chatbot) : `flex-1 min-w-0`
 *     strict, `fontSize: 16px` INLINE (anti-zoom iOS critique), bouton
 *     `shrink-0 w-10 h-10`, `enterKeyHint="send"`, `name="chat-message"`,
 *     `type="button"` (jamais dans un <form>).
 *   - Embed mode 100% passif : pas de position:fixed, pas de visualViewport,
 *     pas de scrollTo. Le parent (car-main.ch) pilote la hauteur via JS.
 *   - Bouton ☎️ : `target="_top"` + fallback JS `window.top.location.href`
 *     pour fonctionner dans 100% des contextes iframe iOS Safari.
 *
 * URL :
 *   - /widget/rdv-ia                 → standalone (page complète)
 *   - /widget/rdv-ia?embed=1         → iframe avec header complet
 *   - /widget/rdv-ia?embed=1&host=1  → iframe SANS header interne (le wrapper
 *                                       parent gère le header) — résout le
 *                                       doublon de header sans modif parent
 *
 * Pas d'auth (public). Conversation autonome avec Claude Sonnet 4.5.
 */
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { Loader2, Send, Bot, CheckCircle2, AlertTriangle, Phone, ArrowLeft, Lock, Calendar, Wrench, Disc, Car, ClipboardCheck, HelpCircle, ChevronLeft, ChevronRight, Snowflake, Sparkles, Settings, Zap, Battery, Fuel, RotateCcw, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PHONE_DISPLAY = "022 980 93 93";
const PHONE_TEL = "+41229809393";
// iter325 — Même numéro sur WhatsApp : le client peut continuer la prise de
// rendez-vous dans son app de messagerie (Léa y répond avec les mêmes tarifs).
const WHATSAPP_URL = "https://wa.me/41229809393?text=" +
  encodeURIComponent("Bonjour, je souhaite prendre un rendez-vous au garage.");

const WELCOME = "Bonjour ! 😊 Je suis Léa, l'assistante de CarMaintenance Meyrin.\n\nDis-moi ce qu'il te faut (entretien, freins, carrosserie, contrôle technique…) et la date qui t'arrange.";

// iter132.e / iter139 — Contextualisation par page-source (brief car-main.ch).
// Mapping : ?source=<slug> → premier message orienté + filtre catégorie + chip "Autre".
//
// Format unifié du message d'accueil :
//   "Bonjour, je vois que vous recherchez un service en <pageName>,
//    je vais vous aider à planifier ce rendez-vous en seulement quelques clics."
//
// Page principale (car-main.ch/garage-meyrin) : aucun ?source= → SOURCE_CONTEXT=null
// → comportement par défaut (toutes catégories, message générique).
const SOURCE_CONTEXTS = {
  mecanique: {
    pageName: "Mécanique",
    categoryHints: ["mécanique", "entretien", "freins"],
  },
  diagnostic: {
    pageName: "Diagnostic électronique",
    categoryHints: ["diagnostic", "électronique"],
  },
  pneus: {
    pageName: "Pneus & Géométrie",
    categoryHints: ["pneu", "géométrie", "pneumatique"],
  },
  climatisation: {
    pageName: "Climatisation",
    categoryHints: ["climatisation", "clim"],
  },
  expertise: {
    pageName: "Expertise / Contrôle technique",
    categoryHints: ["expertise", "contrôle", "technique", "san"],
  },
  carrosserie: {
    pageName: "Carrosserie & Peinture",
    categoryHints: ["carrosserie", "peinture"],
  },
  "boites-vitesses": {
    pageName: "Boîtes de vitesses",
    categoryHints: ["boîte", "vitesses", "embrayage", "boites"],
  },
};

function buildWelcome(pageName) {
  return `Bonjour ! 👋 Je vois que vous recherchez un service en ${pageName}. Je vais vous aider à planifier ce rendez-vous en seulement quelques clics.`;
}

// Lit le paramètre `?source=` au mount et retourne le contexte associé.
// Fallback robuste : si absent ou inconnu, retourne `null` (message générique).
function readSourceContext() {
  try {
    const slug = new URLSearchParams(window.location.search).get("source");
    if (!slug) return null;
    const ctx = SOURCE_CONTEXTS[slug.toLowerCase()];
    if (!ctx) return null;
    return { slug: slug.toLowerCase(), ...ctx, welcome: buildWelcome(ctx.pageName) };
  } catch {
    return null;
  }
}

const SOURCE_CONTEXT = readSourceContext();
const INITIAL_WELCOME = SOURCE_CONTEXT ? SOURCE_CONTEXT.welcome : WELCOME;

// iter114 — Catégories chargées dynamiquement depuis l'admin. Mapping icon → composant lucide.
const ICON_MAP = {
  Wrench, Disc, Car, ClipboardCheck, HelpCircle, Snowflake, Sparkles,
  Settings, Zap, Battery, Fuel, Calendar, Phone,
};
const ICON_NAMES = Object.keys(ICON_MAP);
function resolveIcon(name) {
  return ICON_MAP[name] || HelpCircle;
}
// Fallback statique si l'API n'a pas chargé (cohérence avec le seed serveur)
const FALLBACK_CHIPS = [
  { id: "f1", label: "Entretien", chip_text: "Bonjour, j'aimerais faire un entretien.", icon: "Wrench", subtitle: "vidange, révision", subcategories: [], info_text: "", duree_minutes_defaut: 90 },
  { id: "f2", label: "Freins", chip_text: "Bonjour, j'ai un souci de freins.", icon: "Disc", subtitle: "plaquettes, disques", subcategories: [], info_text: "", duree_minutes_defaut: 120 },
  { id: "f3", label: "Carrosserie", chip_text: "Bonjour, j'ai besoin d'une réparation carrosserie.", icon: "Car", subtitle: "rayure, choc", subcategories: [], info_text: "", duree_minutes_defaut: 240 },
  { id: "f4", label: "Contrôle technique", chip_text: "Bonjour, j'aimerais passer le contrôle technique (expertise).", icon: "ClipboardCheck", subtitle: "expertise OCV", subcategories: [], info_text: "", duree_minutes_defaut: 60 },
  { id: "f5", label: "Autre service", chip_text: "Bonjour, j'ai un autre besoin à te décrire.", icon: "HelpCircle", subtitle: "diagnostic, pneus…", is_other: true, subcategories: [], info_text: "", duree_minutes_defaut: 60 },
];

// Détection embed (iframe ou ?embed=1)
function detectEmbed() {
  try {
    if (window.location.search.includes("embed=1")) return true;
    return window.self !== window.top;
  } catch {
    // cross-origin throw → on est forcément dans une iframe
    return true;
  }
}

// iter103 / iter140 — Détection mode "host parent gère son propre header"
// Conditions (au moins UNE) :
//   - paramètre `?host=1` explicite dans l'URL
//   - détection automatique iframe (window.parent !== window) : si on est
//     embarqué, le parent a forcément son propre header → on masque le nôtre.
function detectHostMode() {
  try {
    if (window.location.search.includes("host=1")) return true;
    // Auto-détection iframe : si on est embed, le parent gère son header.
    // (cross-origin sur window.parent → throw → catch → on est en iframe)
    if (window.parent && window.parent !== window) return true;
  } catch {
    // cross-origin throw → on est forcément dans une iframe
    return true;
  }
  return false;
}

function formatTime(d) {
  const dt = d instanceof Date ? d : new Date(d);
  return dt.toLocaleTimeString("fr-CH", { hour: "2-digit", minute: "2-digit" });
}

// Joli libellé jour "Lun 15 juin"
function formatShortDay(iso) {
  const dt = new Date(iso);
  const days = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
  const months = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
  return `${days[dt.getDay()]} ${dt.getDate()} ${months[dt.getMonth()]}`;
}

// Libellé long pour le payload Léa "lundi 15 juin"
function formatLongDay(iso) {
  const dt = new Date(iso);
  const days = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
  const months = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
  return `${days[dt.getDay()]} ${dt.getDate()} ${months[dt.getMonth()]}`;
}

const INITIAL_MESSAGES = [{ role: "assistant", content: INITIAL_WELCOME, ts: null }];

// iter112 — Mini-calendrier inline pour "Choisir une autre date"
// Affiche les jours fériés genevois + week-ends grisés (cadenas non-cliquable).
// iter150 — Grise aussi les dates avant `minBookableDate` (cutoff serveur).
function InlineCalendar({ month, closedDates, minBookableDate, selectedDate, onPrev, onNext, onPick }) {
  const { year, month: mIdx } = month;
  const firstDay = new Date(year, mIdx, 1);
  const lastDay = new Date(year, mIdx + 1, 0);
  const totalDays = lastDay.getDate();
  // Lundi = 0 dans la grille — JS getDay() : dim=0, lun=1, ..., sam=6
  const startOffset = (firstDay.getDay() + 6) % 7;
  const monthNames = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
  const weekdays = ["L", "M", "M", "J", "V", "S", "D"];
  const today = new Date();
  const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  // iter150 — Si minBookableDate non chargée encore, fallback sur today (comportement v1).
  const minIso = minBookableDate || todayIso;

  // Construire la grille (6 lignes max)
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) {
    const iso = `${year}-${String(mIdx + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ iso, day: d });
  }
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm" data-testid="ia-inline-calendar">
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={onPrev}
          className="w-8 h-8 inline-flex items-center justify-center rounded-lg hover:bg-gray-100 active:scale-95"
          style={{ touchAction: "manipulation" }}
          data-testid="ia-cal-prev"
          aria-label="Mois précédent"
        >
          <ChevronLeft className="w-4 h-4 text-gray-700" />
        </button>
        <div className="text-[13px] font-semibold text-gray-800" data-testid="ia-cal-title">
          {monthNames[mIdx]} {year}
        </div>
        <button
          type="button"
          onClick={onNext}
          className="w-8 h-8 inline-flex items-center justify-center rounded-lg hover:bg-gray-100 active:scale-95"
          style={{ touchAction: "manipulation" }}
          data-testid="ia-cal-next"
          aria-label="Mois suivant"
        >
          <ChevronRight className="w-4 h-4 text-gray-700" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {weekdays.map((w, i) => (
          <div key={i} className="text-center text-[10px] font-semibold text-gray-500 py-1">{w}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map((cell, i) => {
          if (!cell) return <div key={i} />;
          const closed = closedDates[cell.iso];
          const isPast = cell.iso < minIso;
          const isSel = selectedDate === cell.iso;
          const disabled = closed || isPast;
          const title = closed
            ? `Fermé — ${closed.reason || (closed.holiday_name || "indisponible")}`
            : (isPast ? "Délai trop court — choisissez une date ultérieure" : "");
          return (
            <button
              key={cell.iso}
              type="button"
              onClick={() => !disabled && onPick(cell.iso)}
              disabled={disabled}
              title={title}
              className={`aspect-square flex flex-col items-center justify-center rounded-lg text-[12px] font-medium relative ${
                isSel
                  ? "bg-[#52a675] text-white"
                  : disabled
                  ? "bg-gray-50 text-gray-300 cursor-not-allowed"
                  : "bg-white text-gray-800 hover:bg-[#52a675]/10 active:scale-95"
              }`}
              style={{ touchAction: "manipulation", transition: "background-color 0.15s" }}
              data-testid={`ia-cal-day-${cell.iso}`}
              aria-disabled={disabled ? "true" : "false"}
            >
              <span>{cell.day}</span>
              {closed && (
                <Lock className="absolute top-1 right-1 w-2.5 h-2.5 text-gray-400" />
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-2 flex items-center gap-3 text-[10px] text-gray-500">
        <span className="inline-flex items-center gap-1"><Lock className="w-2.5 h-2.5" /> Fermé / férié</span>
        <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-[#52a675]"></span> Sélectionné</span>
      </div>
    </div>
  );
}

export default function PublicRdvIa() {
  const [isEmbed] = useState(detectEmbed);
  const [isHost] = useState(detectHostMode); // iter103 — masque le header interne si ?host=1

  const [messages, setMessages] = useState(INITIAL_MESSAGES);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [extracted, setExtracted] = useState({});
  const [booked, setBooked] = useState(null);
  const [bookError, setBookError] = useState(null);
  const [slotSuggestions, setSlotSuggestions] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null); // iter102 — date choisie → afficher matin/après-midi
  const [showServices, setShowServices] = useState(true); // iter102 — chips services visibles au 1er message

  // iter129 — Étape "Véhicule de remplacement"
  const [wantsLoaner, setWantsLoaner] = useState(null); // null | true | false
  const [loanerOptions, setLoanerOptions] = useState([]); // [{id, marque, modele, plaque, tarif_jour_effectif, courtoisie}]
  const [loanerLoading, setLoanerLoading] = useState(false);
  const [selectedFlotteId, setSelectedFlotteId] = useState(null);
  const [flotteAvailableDates, setFlotteAvailableDates] = useState(null); // Set des dates avec dispo flotte | null = pas chargé
  const [busy8s, setBusy8s] = useState(false); // anti double-clic global (8s)
  const busy8sTimer = useRef(null);
  const triggerBusy8s = useCallback((ms = 8000) => {
    setBusy8s(true);
    if (busy8sTimer.current) clearTimeout(busy8sTimer.current);
    busy8sTimer.current = setTimeout(() => setBusy8s(false), ms);
  }, []);
  useEffect(() => () => { if (busy8sTimer.current) clearTimeout(busy8sTimer.current); }, []);
  const messagesRef = useRef(null);
  const inputRef = useRef(null);
  const dateInputRef = useRef(null); // iter102 — input date natif pour "Autre date"

  // ------------------------------------------------------------------
  // iter93 — Viewport / overflow lock — STANDALONE UNIQUEMENT
  // En mode embed, on laisse la page parente piloter le viewport.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (isEmbed) return;
    const orig = document.querySelector('meta[name="viewport"]');
    const origContent = orig?.getAttribute("content") || "";
    if (orig) {
      orig.setAttribute(
        "content",
        "width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover, interactive-widget=resizes-content",
      );
    }
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    const prevBodyPosition = document.body.style.position;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    document.body.style.overscrollBehavior = "none";
    return () => {
      if (orig && origContent) orig.setAttribute("content", origContent);
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
      document.body.style.position = prevBodyPosition;
      document.body.style.overscrollBehavior = "";
      document.documentElement.style.removeProperty("--lea-vh");
    };
  }, [isEmbed]);

  // ------------------------------------------------------------------
  // iter93 — Hauteur dynamique VisualViewport — STANDALONE UNIQUEMENT
  // En mode embed, c'est l'iframe qui dimensionne et la page parente
  // qui gère le clavier (zéro tremblement).
  // ------------------------------------------------------------------
  useEffect(() => {
    if (isEmbed) return;
    const vv = window.visualViewport;
    const update = () => {
      const h = vv ? vv.height : window.innerHeight;
      document.documentElement.style.setProperty("--lea-vh", `${Math.round(h)}px`);
      window.scrollTo(0, 0);
      const el = messagesRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    };
    update();
    vv?.addEventListener("resize", update);
    vv?.addEventListener("scroll", update);
    window.addEventListener("orientationchange", update);
    window.addEventListener("resize", update);
    return () => {
      vv?.removeEventListener("resize", update);
      vv?.removeEventListener("scroll", update);
      window.removeEventListener("orientationchange", update);
      window.removeEventListener("resize", update);
    };
  }, [isEmbed]);

  // Charge créneaux libres dès l'ouverture (iter107 — limit augmenté pour pagination locale)
  // iter114bis — rechargement à chaque ouverture du calendrier pour refléter les overrides admin
  const loadSlots = useCallback(() => {
    axios.get(`${BACKEND_URL}/api/public/ia-rdv/slots?limit=180`)
      .then(({ data }) => setSlotSuggestions(data.slots || []))
      .catch(() => {});
  }, []);
  useEffect(() => { loadSlots(); }, [loadSlots]);

  // iter112 — Charge dates fermées (week-ends + fériés Genève + overrides admin) pour le mini-calendrier inline
  const [closedDates, setClosedDates] = useState({}); // { "2026-04-06": { reason, is_weekend, is_holiday, holiday_name } }
  const [showCalendar, setShowCalendar] = useState(false);
  // iter150 — Date la plus proche acceptable côté serveur (cutoff Zurich).
  // Avant 16h → J+1 ouvré, après 16h → J+2 ouvré. Utilisée pour griser les
  // dates passées + dont le cutoff est dépassé dans le calendrier ET pour
  // ajuster "Première disponibilité" si elle pointe sur une date refusée.
  const [minBookableDate, setMinBookableDate] = useState(null);

  // iter114 — Catégories dynamiques chargées depuis l'admin
  const [categories, setCategories] = useState(FALLBACK_CHIPS);
  const [checkupConfig, setCheckupConfig] = useState(null);
  const [expandedCatId, setExpandedCatId] = useState(null); // catégorie dont les sous-cat sont visibles
  const [selectedSubcat, setSelectedSubcat] = useState(null); // {category, subcategory}
  const [checkupAccepted, setCheckupAccepted] = useState(null); // null | true | false
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/lea/categories`)
      .then(({ data }) => {
        if (Array.isArray(data?.categories) && data.categories.length > 0) {
          setCategories(data.categories);
        }
        if (data?.checkup) setCheckupConfig(data.checkup);
      })
      .catch(() => {});
  }, []);
  // iter139 — Sur page contextualisée : on N'AFFICHE QUE les catégories matchant
  // les `categoryHints`, et on cache les autres derrière une chip "Autre service".
  // Sur la page principale (pas de ?source=) → comportement inchangé.
  const [showOtherServices, setShowOtherServices] = useState(false);
  const { contextMatches, contextOthers } = useMemo(() => {
    if (!SOURCE_CONTEXT || !SOURCE_CONTEXT.categoryHints || !Array.isArray(categories)) {
      return { contextMatches: categories, contextOthers: [] };
    }
    const hints = SOURCE_CONTEXT.categoryHints.map((h) => h.toLowerCase());
    const matches = [];
    const others = [];
    for (const c of categories) {
      const label = (c.label || "").toLowerCase();
      const sub = (c.subtitle || "").toLowerCase();
      if (hints.some((h) => label.includes(h) || sub.includes(h))) {
        matches.push(c);
      } else {
        others.push(c);
      }
    }
    return { contextMatches: matches, contextOthers: others };
  }, [categories]);

  const categoriesDisplay = useMemo(() => {
    if (!SOURCE_CONTEXT) return categories;
    // Sur page contextualisée : matches OU matches+others si "Autre" cliqué
    return showOtherServices ? [...contextMatches, ...contextOthers] : contextMatches;
  }, [categories, contextMatches, contextOthers, showOtherServices]);

  const [calMonth, setCalMonth] = useState(() => {
    const t = new Date(); return { year: t.getFullYear(), month: t.getMonth() };
  });
  // iter112/113 — Quand la 1ère dispo est connue, recale le calendrier sur son mois
  //               UNE SEULE FOIS (sinon écrase la navigation utilisateur).
  const calInitializedRef = useRef(false);
  useEffect(() => {
    if (calInitializedRef.current) return;
    if (slotSuggestions.length === 0) return;
    const first = slotSuggestions[0]?.date;
    if (!first) return;
    const d = new Date(first);
    setCalMonth({ year: d.getFullYear(), month: d.getMonth() });
    calInitializedRef.current = true;
  }, [slotSuggestions]);
  // iter112/114bis — Charge dates fermées (week-ends + fériés Genève + overrides admin)
  // au mount, ET re-charge à chaque ouverture du calendrier (capture les overrides
  // créés/supprimés en admin pendant que la session est ouverte).
  const loadClosedDates = useCallback(() => {
    axios.get(`${BACKEND_URL}/api/public/ia-rdv/closed-dates?days=120`)
      .then(({ data }) => {
        const map = {};
        (data.closed || []).forEach((c) => {
          if (!c.forced_open) map[c.date] = c;
        });
        setClosedDates(map);
        // iter150 — Synchronise la min-date du cutoff serveur côté client.
        if (data.min_bookable_date) setMinBookableDate(data.min_bookable_date);
      })
      .catch(() => {});
  }, []);
  useEffect(() => { loadClosedDates(); }, [loadClosedDates]);
  useEffect(() => {
    if (showCalendar) { loadClosedDates(); loadSlots(); }
  }, [showCalendar, loadClosedDates, loadSlots]);

  // iter109/iter138 — Timer auto-envoi 1.5s avec indicateur visuel discret :
  //   - réduit de 8000ms → 1500ms (l'utilisateur ne croit plus à un bug)
  //   - feedback léger via `autoSendPending` (petit texte gris "Envoi en cours...")
  //   - utilise setTimeout (zéro re-render par seconde)
  const timerRef = useRef(null);
  const draftRef = useRef("");
  const [autoSendPending, setAutoSendPending] = useState(false);
  useEffect(() => { draftRef.current = draft; }, [draft]);

  // iter108 — Sélections multi-chips visuelles (sans pollution du draft).
  //   Service / date / créneau sont conservés en state, le message est composé
  //   à l'envoi. Si les 3 sont remplis → envoi immédiat (skip countdown).
  const [selectedService, setSelectedService] = useState(null); // {label, text}
  const [selectedCreneau, setSelectedCreneau] = useState(null); // "matin" | "apres_midi"

  const composeFromSelections = useCallback(() => {
    const parts = [];
    if (selectedService) parts.push(selectedService.text);
    // iter114 — Si sous-cat choisie, l'inclure dans le message (avec prix configuré)
    if (selectedSubcat) {
      const sub = selectedSubcat.subcategory;
      if (sub.unknown) {
        parts.push("(Je ne suis pas sûr du détail, à déterminer au garage)");
      } else {
        const priceStr = sub.prix != null ? ` (${Number(sub.prix).toFixed(2)} CHF)` : "";
        parts.push(`Option : ${sub.label}${priceStr}`);
      }
    }
    if (checkupAccepted === true && checkupConfig?.actif) {
      parts.push(`+ check-up complet (${Number(checkupConfig.prix).toFixed(2)} CHF)`);
    } else if (checkupAccepted === false) {
      // Pas mentionné explicitement
    }
    if (selectedDate) parts.push(`Le ${formatLongDay(selectedDate)}`);
    if (selectedCreneau) parts.push(selectedCreneau === "matin" ? "le matin" : "l'après-midi");
    // iter149 — Mentionne explicitement le choix loaner pour que Léa LLM en parle
    // dans la confirmation. Avant cette itération, l'info ne quittait jamais le widget.
    if (wantsLoaner === true) {
      const veh = (loanerOptions || []).find((v) => v.id === selectedFlotteId);
      if (veh) {
        const labelParts = [veh.marque, veh.modele].filter(Boolean).join(" ");
        const plaque = veh.plaque ? ` (${veh.plaque})` : "";
        parts.push(`+ véhicule de remplacement : ${labelParts}${plaque}`);
      } else {
        parts.push("+ véhicule de remplacement souhaité");
      }
    } else if (wantsLoaner === false) {
      parts.push("(sans véhicule de remplacement)");
    }
    return parts.join(" ").trim();
  }, [selectedService, selectedSubcat, checkupAccepted, checkupConfig, selectedDate, selectedCreneau, wantsLoaner, selectedFlotteId, loanerOptions]);

  const cancelAutoSend = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setAutoSendPending(false);
    // iter147 — Permet à l'utilisateur de re-déclencher l'envoi après annulation
    // (sinon la signature reste posée et le timer ne se réarme jamais).
    lastAutoSentSignatureRef.current = null;
  }, []);

  // iter107 — Cleanup timer au démontage
  useEffect(() => () => cancelAutoSend(), [cancelAutoSend]);

  // iter150 — Reset complet de la conversation (bouton "Recommencer").
  // Garantit qu'aucun état "coincé" (date refusée par le cutoff serveur,
  // véhicule de prêt indispo, etc.) ne bloque l'utilisateur. Conserve la
  // session ID pour éviter de re-créer un identifiant côté backend.
  const handleRestart = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    setAutoSendPending(false);
    lastAutoSentSignatureRef.current = null;
    // iter150 — Coupe aussi un éventuel envoi en cours (sinon "sending=true" cache
    // les chips services après le reset jusqu'à ce que la réponse Léa arrive).
    setSending(false);
    sendingRef.current = false;
    setMessages(INITIAL_MESSAGES);
    setDraft("");
    draftRef.current = "";
    setExtracted({});
    setBooked(null);
    setBookError(null);
    setSelectedDate(null);
    setSelectedCreneau(null);
    setSelectedService(null);
    setSelectedSubcat(null);
    setCheckupAccepted(null);
    setExpandedCatId(null);
    setWantsLoaner(null);
    setSelectedFlotteId(null);
    setLoanerOptions([]);
    setFlotteAvailableDates(null);
    setShowCalendar(false);
    setShowServices(true);
    setDaysShown(5);
  }, []);

  // iter107 — Pagination "Voir plus de dates" : 5 jours visibles à la fois
  // (pas de reset sur changement de slotSuggestions — l'utilisateur garde son choix d'affichage)
  const [daysShown, setDaysShown] = useState(5);

  const scrollToBottom = useCallback((smooth = true) => {
    const el = messagesRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
  }, []);

  // iter109/iter139 — Scroll auto sur events critiques.
  // iter139 : ajoute les transitions d'état qui font APPARAÎTRE un nouveau bloc
  // (sous-cat, check-up, loaner, slots, véhicules de remplacement). Petit
  // requestAnimationFrame pour laisser le repaint avant le scroll.
  useEffect(() => {
    const id = requestAnimationFrame(() => scrollToBottom());
    return () => cancelAnimationFrame(id);
  }, [
    messages.length, !!booked, !!bookError, sending, scrollToBottom,
    // iter139 — déclenche aussi quand Léa propose un nouvel élément :
    !!selectedService,            // sous-cat / check-up / loaner s'affichent
    !!selectedSubcat,             // check-up ou loaner s'affichent
    checkupAccepted,              // loaner s'affiche
    wantsLoaner,                  // véhicules de remplacement / slots s'affichent
    slotSuggestions.length,       // les jours apparaissent
    expandedCatId,                // bloc sous-cat s'agrandit
  ]);

  // iter93 — Focus input — STANDALONE UNIQUEMENT (en embed, le navigateur gère)
  const handleInputFocus = () => {
    if (isEmbed) return;
    setTimeout(() => scrollToBottom(false), 300);
    setTimeout(() => {
      window.scrollTo(0, 0);
      scrollToBottom(false);
    }, 650);
  };

  // iter102 — Fermeture iframe (mode embed uniquement)
  const handleClose = () => {
    try {
      // Format objet (recommandé pour parent)
      window.parent.postMessage({ type: "lea:close" }, "*");
      // Format string (rétrocompat avec un parent qui écoute simplement 'close')
      window.parent.postMessage("close", "*");
    } catch {
      // pas d'iframe ou parent inaccessible — no-op
    }
  };

  // iter108bis — Verrou ref-based contre le double-envoi (race condition entre l'effet
  // "3 sélections faites" et le timer countdown 8s qui pouvait fire en même temps,
  // car `sending` (state) est asynchrone et ne bloquait pas le 2e appel concurrent)
  const sendingRef = useRef(false);

  const handleSend = async (textOverride) => {
    const txt = (typeof textOverride === "string" ? textOverride : draft).trim();
    if (!txt || sending || booked) return;
    if (sendingRef.current) return;          // iter108bis — bloque tout 2e envoi concurrent
    sendingRef.current = true;
    // iter107/109/138 — cancel any pending auto-send timer + masque l'indicateur
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    setAutoSendPending(false);
    setSending(true);
    setBookError(null);
    setShowServices(false); // iter102 — chips services masquées dès le 1er envoi
    const userMsg = { role: "user", content: txt, ts: new Date().toISOString() };
    const newHistory = [...messages, userMsg];
    setMessages(newHistory);
    setDraft("");
    if (inputRef.current) inputRef.current.style.height = "44px";
    inputRef.current?.focus();
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/public/ia-rdv/chat`, {
        session_id: sessionId,
        message: txt,
        history: newHistory.map((m) => ({ role: m.role, content: m.content })),
      }, { timeout: 60000 });

      if (data.session_id) setSessionId(data.session_id);
      const merged = { ...extracted, ...(data.extracted || {}) };
      Object.keys(extracted).forEach((k) => {
        if (extracted[k] && !merged[k]) merged[k] = extracted[k];
      });
      setExtracted(merged);

      setMessages((m) => [...m, {
        role: "assistant",
        content: data.reply || "...",
        ts: new Date().toISOString(),
      }]);
      setSlotSuggestions(data.slot_suggestions || []);
      // iter102 — Si Léa retourne des slots ou attend une date, on reset selectedDate
      // iter108 — On reset toutes les sélections + draft pour repartir propre
      if (data.slot_suggestions && data.slot_suggestions.length > 0) {
        setSelectedDate(null);
        setSelectedService(null);
        setSelectedCreneau(null);
        draftRef.current = "";
      } else {
        setSelectedService(null);
        setSelectedCreneau(null);
        setSelectedDate(null);
        draftRef.current = "";
      }

      if (data.ready_to_book) {
        await new Promise((r) => setTimeout(r, 400));
        await attemptBooking(merged);
      }
    } catch (e) {
      toast.error(`L'assistante est temporairement indisponible. Réessaie ou appelle au ${PHONE_DISPLAY}.`);
      setMessages((m) => [...m, {
        role: "assistant",
        content: "Pardon, j'ai eu un souci technique. Tu peux réessayer ?",
        ts: new Date().toISOString(),
      }]);
    } finally {
      setSending(false);
      sendingRef.current = false;    // iter108bis — libère le verrou anti-double-envoi
    }
  };

  const attemptBooking = async (data) => {
    setSending(true);
    try {
      const payload = {
        type_intervention_id: data.type_intervention_id,
        date_souhaitee: data.date_souhaitee,
        creneau_souhaite: data.creneau_souhaite,
        type_service: data.type_label || "Service",
        nom: data.nom,
        prenom: data.prenom,
        telephone: data.telephone,
        plaque: data.plaque || "",
        marque: data.marque || "",
        modele: data.modele || "",
        message: data.message || "Pris via assistante IA",
        // iter129 — Véhicule de remplacement (si client a choisi "Oui" et sélectionné un véhicule)
        vehicule_remplacement_souhaite: wantsLoaner === true,
        flotte_id_souhaite: selectedFlotteId || null,
      };
      const { data: res } = await axios.post(
        `${BACKEND_URL}/api/public/rdv/demande-auto`,
        { ...payload, ia_session_id: sessionId },
        { timeout: 30000 },
      );
      setBooked({
        numero: res.rdv_id || res.numero || "—",
        date: payload.date_souhaitee,
        creneau: payload.creneau_souhaite,
        nom: `${payload.prenom} ${payload.nom}`,
        type: payload.type_service,
      });
      // iter132.e — postMessage vers car-main.ch pour le tracking GA4 par page-source
      try {
        if (window.parent && window.parent !== window) {
          window.parent.postMessage({
            type: "lea_rdv_complete",
            source: SOURCE_CONTEXT?.slug || "unknown",
            service: payload.type_service || "—",
            rdv_id: res.rdv_id || res.numero || null,
          }, "*");
        }
      } catch { /* parent cross-origin ou widget standalone : silencieux */ }
      setMessages((m) => [...m, {
        role: "assistant",
        content: `Parfait ${payload.prenom} ! Ton rendez-vous est confirmé. Tu recevras un SMS de confirmation au ${payload.telephone}. À bientôt 😊`,
        ts: new Date().toISOString(),
      }]);
    } catch (e) {
      const rawDetail = e.response?.data?.detail;
      if (rawDetail && typeof rawDetail === "object" && rawDetail.code === "doublon_rdv") {
        setBookError(rawDetail.message);
        setMessages((m) => [...m, { role: "assistant", content: rawDetail.message, ts: new Date().toISOString() }]);
        return;
      }
      const detail = typeof rawDetail === "string" ? rawDetail : "Erreur lors de la création du RDV";
      setBookError(detail);
      setMessages((m) => [...m, {
        role: "assistant",
        content: `Oups, je n'arrive pas à finaliser le RDV : ${detail}. Tu veux essayer une autre date ?`,
        ts: new Date().toISOString(),
      }]);
    } finally { setSending(false); }
  };

  // iter102 — Regroupe les slots par date pour afficher des chips jour uniques.
  // iter107 — Plus de limite à 4 — on retourne TOUS les jours uniques, la pagination
  // se fait via `daysShown` à l'affichage.
  const daysFromSlots = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const s of slotSuggestions) {
      if (seen.has(s.date)) continue;
      seen.add(s.date);
      out.push({ date: s.date, date_label: s.date_label });
    }
    return out;
  }, [slotSuggestions]);

  // iter102 — Pour un jour donné, retourne les créneaux dispo (matin/après-midi)
  const cresForDay = (iso) => slotSuggestions.filter((s) => s.date === iso);

  // iter138/iter140 — setTimeout court (1.5s) avec feedback visuel discret.
  // iter140 : ne démarre PAS le timer tant que toutes les étapes obligatoires
  // ne sont pas remplies (sous-cat, check-up, loaner, véhicule de remplacement,
  // date, créneau). Évite l'auto-envoi prématuré avant que l'utilisateur ait
  // répondu à la question "véhicule de prêt ?".
  const areAllRequiredSelectionsMade = useCallback(() => {
    if (!selectedService) return false;
    const cat = categories.find((c) => c.id === selectedService.id);
    // Sous-catégorie obligatoire si la cat en a
    if (cat?.subcategories?.length > 0 && !selectedSubcat) return false;
    // Check-up obligatoire si proposé
    if (cat?.propose_checkup_upsell && checkupConfig?.actif && checkupAccepted === null) return false;
    // Véhicule de remplacement : réponse obligatoire (iter129)
    if (wantsLoaner === null) return false;
    // Si loaner=true → choix d'un véhicule obligatoire
    if (wantsLoaner === true && !selectedFlotteId) return false;
    // Date + créneau obligatoires
    if (!selectedDate || !selectedCreneau) return false;
    return true;
  }, [selectedService, selectedSubcat, checkupAccepted, checkupConfig, categories, wantsLoaner, selectedFlotteId, selectedDate, selectedCreneau]);

  const scheduleAutoSend = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    // iter140 — Bloque le timer si une étape obligatoire manque encore
    if (!areAllRequiredSelectionsMade()) {
      setAutoSendPending(false);
      return;
    }
    setAutoSendPending(true);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setAutoSendPending(false);
      if (sendingRef.current) return;
      const composed = draftRef.current || "";
      if (composed) handleSend(composed);
    }, 1500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areAllRequiredSelectionsMade]);

  // iter108 — Reset draftRef aussi quand les sélections changent (pour que le timer envoie le bon texte)
  useEffect(() => {
    const txt = composeFromSelections();
    if (txt) draftRef.current = txt;
  }, [composeFromSelections]);

  const handlePickDay = (iso) => {
    if (sending || booked) return;
    setSelectedDate((cur) => (cur === iso ? null : iso));
    // si on désélectionne le jour, on désélectionne aussi le créneau
    if (selectedDate === iso) setSelectedCreneau(null);
    // Si toutes les sélections sont OK après ce clic, envoi direct (utilise effet ci-dessous)
    scheduleAutoSend();
  };

  const handlePickCreneau = (iso, creneau) => {
    if (sending || booked) return;
    setSelectedCreneau(creneau);
    // Pas besoin de fermer selectedDate (l'utilisateur peut changer d'avis)
    scheduleAutoSend();
  };

  // iter114 — Refs pour scroll-into-view UX (sinon hors écran mobile)
  const calendarRef = useRef(null);
  const subcategoriesRef = useRef(null);
  const checkupRef = useRef(null);
  const creneauRef = useRef(null);
  const handleOtherDate = () => {
    if (sending || booked) return;
    setShowCalendar((v) => !v);
  };
  // iter114bis — Scroll auto vers le calendrier dès qu'il s'ouvre (sinon hors écran sur mobile)
  useEffect(() => {
    if (!showCalendar) return;
    const t = setTimeout(() => {
      const el = calendarRef.current;
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 60);
    return () => clearTimeout(t);
  }, [showCalendar]);
  // iter114bis — Scroll vers sous-catégories quand une catégorie est étendue
  useEffect(() => {
    if (!expandedCatId) return;
    const t = setTimeout(() => {
      const el = subcategoriesRef.current;
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }, 60);
    return () => clearTimeout(t);
  }, [expandedCatId]);
  // iter114bis — Scroll vers matin/après-midi quand selectedDate est posée (date confirmée)
  useEffect(() => {
    if (!selectedDate) return;
    const t = setTimeout(() => {
      const el = creneauRef.current;
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }, 60);
    return () => clearTimeout(t);
  }, [selectedDate]);
  // iter114bis — Scroll vers upsell check-up quand il apparaît
  useEffect(() => {
    if (!selectedSubcat || checkupAccepted !== null) return;
    const cat = selectedSubcat.category;
    if (!cat?.propose_checkup_upsell) return;
    const t = setTimeout(() => {
      const el = checkupRef.current;
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }, 60);
    return () => clearTimeout(t);
  }, [selectedSubcat, checkupAccepted]);

  // iter112 — Navigation mois précédent/suivant dans le mini-calendrier
  const handleCalPrev = () => {
    setCalMonth((m) => {
      const d = new Date(m.year, m.month - 1, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  };
  const handleCalNext = () => {
    setCalMonth((m) => {
      const d = new Date(m.year, m.month + 1, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  };

  // iter112 — Clic sur une date du mini-calendrier
  const handleCalPickDay = (iso) => {
    if (sending || booked) return;
    if (closedDates[iso]) return; // fermé, ignore
    setSelectedDate(iso);
    setShowCalendar(false);
    scheduleAutoSend();
  };

  // iter114bis — Anti-boucle infinie : signature des sélections déjà auto-envoyées.
  // Si le même set de chips a déjà déclenché un envoi (succès OU erreur), on n'envoie pas une 2e fois
  // tant que l'utilisateur n'a pas changé une sélection.
  const lastAutoSentSignatureRef = useRef(null);

  // iter108/114 — Effet : si toutes les sélections requises sont remplies → envoi immédiat
  useEffect(() => {
    if (!selectedService || !selectedDate || !selectedCreneau) return;
    if (sending || booked || sendingRef.current) return;
    // iter114 — Si la catégorie en cours a des sous-cat, on attend que l'utilisateur en choisisse une
    const cat = categories.find((c) => c.id === selectedService.id);
    if (cat?.subcategories?.length > 0 && !selectedSubcat) return;
    // Si l'upsell check-up est proposé pour cette cat, on attend la réponse oui/non/plus_tard
    if (cat?.propose_checkup_upsell && checkupConfig?.actif && checkupAccepted === null) return;
    // iter129 — Bloque l'auto-send tant que le client n'a pas répondu Oui/Non sur véhicule de remplacement
    if (wantsLoaner === null) return;
    // iter129 — Si wantsLoaner=true → exige aussi le choix d'un véhicule de remplacement
    if (wantsLoaner === true && !selectedFlotteId) return;
    // iter114bis — Anti-spam : si cette combinaison a déjà été envoyée, on ne renvoie pas
    const signature = [
      selectedService.id,
      selectedSubcat?.subcategory?.id ?? "none",
      selectedSubcat?.subcategory?.unknown ? "unknown" : "spec",
      checkupAccepted ?? "na",
      selectedDate,
      selectedCreneau,
      wantsLoaner === null ? "na" : (wantsLoaner ? "loaner" : "noloaner"),
      selectedFlotteId ?? "none",
    ].join("|");
    if (lastAutoSentSignatureRef.current === signature) return;
    lastAutoSentSignatureRef.current = signature;
    // iter147 — Aligné à 1500 ms (au lieu de 50 ms) + feedback visuel `autoSendPending`.
    // Le 50 ms court-circuitait le timer 1.5 s officiel et déclenchait l'envoi avant
    // que l'utilisateur ait pu changer d'avis (ex. cliquer "Non" sur véhicule de
    // remplacement après avoir choisi date+créneau).
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    const composed = composeFromSelections();
    if (composed) {
      setAutoSendPending(true);
      const t = setTimeout(() => {
        setAutoSendPending(false);
        handleSend(composed);
      }, 1500);
      timerRef.current = t;
      return () => { clearTimeout(t); setAutoSendPending(false); };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedService, selectedDate, selectedCreneau, selectedSubcat, checkupAccepted, sending, booked, wantsLoaner, selectedFlotteId]);

  // iter129 — Charge les véhicules de remplacement disponibles dès que date+créneau choisis et wantsLoaner=true
  useEffect(() => {
    if (wantsLoaner !== true || !selectedDate || !selectedCreneau) {
      setLoanerOptions([]);
      return;
    }
      const typeId = selectedSubcat?.subcategory?.type_intervention_id
        || selectedService?.type_intervention_id || null;
      const catLabel = (selectedService?.label || "").toLowerCase();
      const serviceCategorie = catLabel.includes("carrosserie") ? "carrosserie" : null;
      setLoanerLoading(true);
      axios.get(`${BACKEND_URL}/api/public/ia-rdv/flotte-dispo-jour`, {
        params: {
          date: selectedDate, creneau: selectedCreneau,
          ...(typeId ? { type_intervention_id: typeId } : {}),
          ...(serviceCategorie ? { service_categorie: serviceCategorie } : {}),
        },
      })
      .then(({ data }) => {
        setLoanerOptions(data.vehicules || []);
        // iter147 — Plus d'auto-sélection même si un seul véhicule dispo :
        // on force l'utilisateur à confirmer son choix avant l'auto-send.
      })
      .catch(() => setLoanerOptions([]))
      .finally(() => setLoanerLoading(false));
  }, [wantsLoaner, selectedDate, selectedCreneau, selectedSubcat, selectedService]);

  // ⬇ ancien handler conservé pour compatibilité (devenu inutile mais ne casse rien)
  const _legacyOpenPicker = () => {
    const inp = dateInputRef.current;
    if (!inp) return;
    if (typeof inp.showPicker === "function") {
      try { inp.showPicker(); return; } catch { /* fallback */ }
    }
    inp.click();
  };

  const handleOtherDateChange = (e) => {
    const iso = e.target.value;
    if (!iso) return;
    setSelectedDate(iso);
  };

  const handleServiceChip = (chip) => {
    if (sending || booked) return;
    // iter110 — "Autre service" / is_other : envoi immédiat, Léa demandera la description
    if (chip.is_other || chip.isOther) {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      setSelectedService(null);
      setSelectedDate(null);
      setSelectedCreneau(null);
      setExpandedCatId(null);
      setSelectedSubcat(null);
      setCheckupAccepted(null);
      handleSend(chip.chip_text || chip.text);
      return;
    }
    // iter114 — Si la catégorie a des sous-cat → expansion inline + reset sous-cat précédente
    const hasSubs = (chip.subcategories && chip.subcategories.length > 0);
    if (hasSubs) {
      // toggle expansion
      setExpandedCatId((cur) => (cur === chip.id ? null : chip.id));
      // Sélectionne la catégorie comme service "courant" (chip vert)
      setSelectedService({ label: chip.label, text: chip.chip_text || chip.text, id: chip.id });
      setSelectedSubcat(null);
      setCheckupAccepted(null);
      return; // ne déclenche pas scheduleAutoSend tant qu'aucune sous-cat choisie
    }
    // iter108/114 — Pas de sous-cat : flow legacy (envoi auto une fois date+créneau)
    setExpandedCatId(null);
    setSelectedSubcat(null);
    setCheckupAccepted(null);
    // iter129 — Reset l'étape véhicule remplacement quand on change de service
    setWantsLoaner(null);
    setSelectedFlotteId(null);
    setFlotteAvailableDates(null);
    setSelectedService((cur) => (cur && cur.label === chip.label ? null : { label: chip.label, text: chip.chip_text || chip.text, id: chip.id }));
    scheduleAutoSend();
  };

  // iter114 — Clic sur une sous-catégorie (avec prix)
  const handleSubcategoryPick = (cat, sub) => {
    if (sending || booked) return;
    setSelectedSubcat({ category: cat, subcategory: sub });
    // iter129 — Reset l'étape véhicule remplacement quand on change de sous-catégorie
    setWantsLoaner(null);
    setSelectedFlotteId(null);
    setFlotteAvailableDates(null);
    // Si la cat propose le check-up upsell et qu'il est actif, on attend la réponse upsell
    if (cat.propose_checkup_upsell && checkupConfig?.actif) {
      setCheckupAccepted(null);
      return;
    }
    // Sinon, on est prêt — l'effet auto-send déclenchera quand date+créneau seront posés
    setCheckupAccepted(false);
    scheduleAutoSend();
  };

  // iter114 — "Je ne sais pas" : sous-cat indéterminée → Léa décide au garage
  const handleSubcategoryUnknown = (cat) => {
    if (sending || booked) return;
    setSelectedSubcat({ category: cat, subcategory: { label: "Je ne sais pas", prix: null, duree_minutes: cat.duree_minutes_defaut || 60, unknown: true } });
    if (cat.propose_checkup_upsell && checkupConfig?.actif) {
      setCheckupAccepted(null);
      return;
    }
    setCheckupAccepted(false);
    scheduleAutoSend();
  };

  // iter114 — Réponse à l'upsell check-up
  const handleCheckupChoice = (accepted) => {
    if (sending || booked) return;
    setCheckupAccepted(accepted);
    scheduleAutoSend();
  };

  const handleDraftChange = (e) => {
    setDraft(e.target.value);
    const ta = e.target;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 110)}px`;
  };

  // iter102 — Date minimum pour le picker "Autre date" = demain
  const minDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  }, []);

  // ====================================================================
  // Layout : 2 modes selon isEmbed
  // iter103 — En mode embed, on retire `overflow: hidden` qui empêchait
  // iOS Safari de scroller la textarea au-dessus du clavier. Le parent
  // (car-main.ch) doit piloter la hauteur via visualViewport (voir doc).
  // ====================================================================
  const containerStyle = isEmbed
    ? {
        // EMBED : le parent gère la hauteur. On reste passif.
        position: "relative",
        height: "100%",
        minHeight: "100%",
        // Pas d'overflow:hidden → iOS peut scroller naturellement au focus
      }
    : {
        // STANDALONE : iter93 — fixed + VisualViewport
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: "var(--lea-vh, 100dvh)",
        maxHeight: "var(--lea-vh, 100dvh)",
        overflow: "hidden",
        overscrollBehavior: "none",
      };

  // iter103 — Handler téléphone avec double sécurité pour iframes iOS Safari
  const handlePhoneClick = (e) => {
    if (isEmbed) {
      // En iframe, <a href="tel:"> est parfois bloqué — on force via top
      try {
        if (window.top && window.top !== window.self) {
          window.top.location.href = `tel:${PHONE_TEL}`;
          e.preventDefault();
          return;
        }
      } catch {
        // Cross-origin → on laisse target=_top faire son boulot
      }
    }
    // Standalone : le navigateur gère naturellement
  };

  return (
    <div
      className="flex flex-col bg-[#eef2ee]"
      style={containerStyle}
      data-testid="ia-rdv-page"
      data-embed={isEmbed ? "1" : "0"}
      data-host={isHost ? "1" : "0"}
    >
      <div
        className="w-full max-w-2xl mx-auto flex flex-col flex-1 min-h-0 bg-white sm:my-0 sm:shadow-lg"
        data-testid="ia-rdv-widget"
      >
        {/* ── Header unifié (masqué si ?host=1) ───────────────────────── */}
        {!isHost && (
        <div
          className="flex items-center gap-2 px-3 py-2 bg-[#2d5f3f] text-white shrink-0"
          style={{ paddingTop: isEmbed ? "8px" : "calc(8px + env(safe-area-inset-top))" }}
        >
          {isEmbed && (
            <button
              type="button"
              onClick={handleClose}
              className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/10 active:bg-white/20 transition-colors"
              aria-label="Fermer le chat"
              data-testid="ia-rdv-back"
            >
              <ArrowLeft className="w-[20px] h-[20px]" />
            </button>
          )}
          <div className="relative shrink-0">
            <div className="w-10 h-10 rounded-full bg-[#52a675] flex items-center justify-center">
              <Bot className="w-5 h-5" strokeWidth={2.2} />
            </div>
            <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-[#2d5f3f]"></span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[15px] leading-tight truncate">Léa · CarMaintenance Meyrin</div>
            <div className="text-[11px] text-emerald-100/90 leading-tight truncate">
              {sending ? "écrit…" : "En ligne · Assistante de rendez-vous"}
            </div>
          </div>
          <a
            href={`tel:${PHONE_TEL}`}
            target="_top"
            rel="noopener"
            onClick={handlePhoneClick}
            className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/10 active:bg-white/20 transition-colors"
            aria-label={`Appeler le garage au ${PHONE_DISPLAY}`}
            data-testid="ia-rdv-call"
          >
            <Phone className="w-[18px] h-[18px]" />
          </a>
          {/* iter150 — Bouton "Recommencer la conversation" : reset complet de l'état.
              Visible dès qu'il y a au moins un échange (sinon inutile). */}
          {messages.length > 1 && (
            <button
              type="button"
              onClick={handleRestart}
              className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/10 active:bg-white/20 transition-colors"
              aria-label="Recommencer la conversation"
              title="Recommencer la conversation"
              data-testid="ia-rdv-restart"
            >
              <RotateCcw className="w-[18px] h-[18px]" />
            </button>
          )}
        </div>
        )}

        {/* ── Sous-titre sécurité ──────────────────────────────────────── */}
        <div
          className="flex items-center justify-center gap-3 px-3 py-1.5 bg-[#eef2ee] border-b border-gray-200 shrink-0 text-[11px] text-gray-600"
          data-testid="ia-rdv-secure-banner"
        >
          <div className="flex items-center gap-1.5">
            <Lock className="w-3 h-3" />
            <span>Discussion sécurisée avec Léa</span>
          </div>
          {/* iter103 — En mode host, on relocalise le ☎️ ici en mini-lien */}
          {isHost && (
            <a
              href={`tel:${PHONE_TEL}`}
              target="_top"
              rel="noopener"
              onClick={handlePhoneClick}
              className="inline-flex items-center gap-1 text-[#2d5f3f] font-medium hover:underline"
              data-testid="ia-rdv-call-host"
            >
              <Phone className="w-3 h-3" />
              <span>{PHONE_DISPLAY}</span>
            </a>
          )}
          {/* iter150 — Bouton "Recommencer" relocalisé ici en mode host */}
          {isHost && messages.length > 1 && (
            <button
              type="button"
              onClick={handleRestart}
              className="inline-flex items-center gap-1 text-[#2d5f3f] font-medium hover:underline"
              data-testid="ia-rdv-restart-host"
            >
              <RotateCcw className="w-3 h-3" />
              <span>Recommencer</span>
            </button>
          )}
        </div>

        {/* ── iter325 — Passerelle WhatsApp (même numéro, même assistante) ── */}
        <a
          href={WHATSAPP_URL}
          target="_top"
          rel="noopener"
          className="flex items-center justify-center gap-2 px-3 py-2 shrink-0 bg-[#e7f6ec] border-b border-[#bfe3cb] text-[12px] font-medium text-[#1f7a46] hover:bg-[#d9f0e2] active:bg-[#cdebd9] transition-colors"
          data-testid="ia-rdv-whatsapp-link"
        >
          <MessageCircle className="w-3.5 h-3.5" />
          <span>Vous préférez WhatsApp ? Écrivez-nous au {PHONE_DISPLAY}</span>
        </a>

        {/* ── Conversation ─────────────────────────────────────────────── */}
        <div
          ref={messagesRef}
          className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-2 bg-[#eef2ee]"
          style={{
            // iter109 — pattern radial supprimé : il déclenchait des repaints
            // coûteux sur iOS Safari pendant l'animation du clavier (cause de
            // frémissements résiduels). Background plat plus performant.
            // -webkit-overflow-scrolling: touch déprécié depuis iOS 13 (retiré aussi)
            overscrollBehaviorY: "contain",
          }}
          data-testid="ia-rdv-messages"
        >
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[85%] px-3 py-2 text-[15px] leading-snug shadow-sm relative ${
                  m.role === "user"
                    ? "bg-[#52a675] text-white rounded-2xl rounded-br-md"
                    : "bg-white text-gray-900 rounded-2xl rounded-bl-md"
                }`}
                data-testid={`ia-msg-${i}`}
                style={{
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                  whiteSpace: "pre-wrap",
                }}
              >
                {m.content}
                {/* iter102 — Timestamp discret */}
                {m.ts && (
                  <span
                    className={`block text-[10px] mt-1 text-right ${
                      m.role === "user" ? "text-white/70" : "text-gray-400"
                    }`}
                    data-testid={`ia-msg-ts-${i}`}
                  >
                    {formatTime(m.ts)}
                  </span>
                )}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-white rounded-2xl rounded-bl-md px-3.5 py-2.5 inline-flex items-center gap-1.5 shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "0ms" }}></span>
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "150ms" }}></span>
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "300ms" }}></span>
              </div>
            </div>
          )}

          {/* iter112/114 — Section 1 : Services dynamiques (icônes + sous-titres) + sous-catégories inline */}
          {!sending && !booked && showServices && messages.length === 1 && (
            <div className="space-y-2 pt-1" data-testid="ia-services-section">
              <div className="text-[12px] font-semibold text-gray-700 pl-1">1. Quel service souhaitez-vous ?</div>
              <div className="grid grid-cols-2 gap-2" data-testid="ia-service-chips">
                {categoriesDisplay.map((c, i) => {
                  const isSel = selectedService && selectedService.id === c.id;
                  const Icon = resolveIcon(c.icon);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => handleServiceChip(c)}
                      className={`flex flex-col items-start gap-1 px-3 py-2.5 rounded-xl border-2 text-left active:scale-95 ${
                        isSel
                          ? "bg-[#52a675] border-[#52a675] text-white shadow"
                          : "bg-white border-gray-200 text-gray-800 hover:border-[#52a675] hover:bg-[#52a675]/5"
                      }`}
                      style={{ touchAction: "manipulation", transition: "background-color 0.15s, color 0.15s, border-color 0.15s" }}
                      data-testid={`ia-service-chip-${i}`}
                      aria-pressed={isSel ? "true" : "false"}
                    >
                      <Icon className={`w-4 h-4 ${isSel ? "text-white" : "text-[#52a675]"}`} />
                      <span className="text-[13px] font-semibold leading-tight">{c.label}</span>
                      {c.subtitle && (
                        <span className={`text-[10px] leading-tight ${isSel ? "text-white/85" : "text-gray-500"}`}>
                          {c.subtitle}
                        </span>
                      )}
                    </button>
                  );
                })}
                {/* iter139 — Chip "Autre service" : visible uniquement sur page contextualisée
                    quand des catégories non-matching existent et ne sont pas encore révélées. */}
                {SOURCE_CONTEXT && !showOtherServices && contextOthers.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowOtherServices(true)}
                    className="flex flex-col items-start gap-1 px-3 py-2.5 rounded-xl border-2 border-dashed border-gray-300 bg-white text-gray-700 hover:border-[#52a675] hover:bg-[#52a675]/5 active:scale-95 text-left"
                    style={{ touchAction: "manipulation", transition: "background-color 0.15s, border-color 0.15s" }}
                    data-testid="ia-service-chip-other"
                  >
                    <HelpCircle className="w-4 h-4 text-[#52a675]" />
                    <span className="text-[13px] font-semibold leading-tight">Autre service</span>
                    <span className="text-[10px] leading-tight text-gray-500">
                      voir tous les services ({contextOthers.length})
                    </span>
                  </button>
                )}
              </div>

              {/* iter114 — Sous-catégories expansibles inline */}
              {expandedCatId && (() => {
                const expCat = categories.find((c) => c.id === expandedCatId);
                if (!expCat || !expCat.subcategories?.length) return null;
                return (
                  <div ref={subcategoriesRef} className="bg-[#52a675]/5 border-2 border-[#52a675]/30 rounded-xl p-3 space-y-2 mt-1" data-testid="ia-subcategories-section">
                    <div className="text-[11px] font-semibold text-[#2d5f3f] uppercase tracking-wide">
                      {expCat.label} — choisis ton option
                    </div>
                    <div className="space-y-1.5">
                      {expCat.subcategories.map((sub) => {
                        const isSubSel = selectedSubcat && selectedSubcat.subcategory.id === sub.id;
                        return (
                          <button
                            key={sub.id}
                            type="button"
                            onClick={() => handleSubcategoryPick(expCat, sub)}
                            className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border-2 text-left active:scale-[0.98] ${
                              isSubSel
                                ? "bg-[#52a675] border-[#52a675] text-white"
                                : "bg-white border-gray-200 text-gray-800 hover:border-[#52a675]"
                            }`}
                            style={{ touchAction: "manipulation", transition: "background-color 0.15s, border-color 0.15s" }}
                            data-testid={`ia-subcategory-${sub.id}`}
                            aria-pressed={isSubSel ? "true" : "false"}
                          >
                            <span className="text-[12px] font-medium leading-tight flex-1">{sub.label}</span>
                            <span className={`text-[13px] font-bold whitespace-nowrap ${isSubSel ? "text-white" : "text-[#2d5f3f]"}`}>
                              {Number(sub.prix).toFixed(0)}.-
                            </span>
                          </button>
                        );
                      })}
                      {/* Bouton "Je ne sais pas" - toujours présent */}
                      <button
                        type="button"
                        onClick={() => handleSubcategoryUnknown(expCat)}
                        className={`w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed text-[12px] active:scale-[0.98] ${
                          selectedSubcat?.subcategory?.unknown
                            ? "bg-gray-200 border-gray-400 text-gray-800"
                            : "bg-white border-gray-300 text-gray-600 hover:border-gray-400"
                        }`}
                        style={{ touchAction: "manipulation", transition: "border-color 0.15s" }}
                        data-testid="ia-subcategory-unknown"
                      >
                        <HelpCircle className="w-3 h-3" />
                        Je ne sais pas
                      </button>
                    </div>
                    {expCat.info_text && (
                      <div className="text-[10.5px] text-gray-600 leading-snug bg-white/60 rounded-md px-2 py-1.5 border border-gray-200" data-testid="ia-subcategory-info">
                        ℹ️ {expCat.info_text}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* iter114 — Upsell check-up (si activé sur la cat + sous-cat sélectionnée + check-up actif) */}
              {selectedSubcat && checkupConfig?.actif && (() => {
                const cat = selectedSubcat.category;
                if (!cat.propose_checkup_upsell) return null;
                return (
                  <div ref={checkupRef} className="bg-amber-50 border-2 border-amber-300 rounded-xl p-3 space-y-2" data-testid="ia-checkup-upsell">
                    <div className="flex items-start gap-2">
                      <Sparkles className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="text-[12px] font-bold text-amber-900 leading-tight">
                          Ajouter le {checkupConfig.label} à {Number(checkupConfig.prix).toFixed(0)}.- ?
                        </div>
                        {checkupConfig.description && (
                          <div className="text-[10.5px] text-amber-800 leading-snug mt-0.5">
                            {checkupConfig.description}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-1.5">
                      <button
                        type="button"
                        onClick={() => handleCheckupChoice(true)}
                        className={`px-2 py-1.5 rounded-lg text-[12px] font-semibold active:scale-95 ${
                          checkupAccepted === true
                            ? "bg-[#52a675] text-white"
                            : "bg-white border-2 border-amber-300 text-amber-900 hover:bg-amber-100"
                        }`}
                        style={{ touchAction: "manipulation", transition: "background-color 0.15s" }}
                        data-testid="ia-checkup-yes"
                      >
                        Oui
                      </button>
                      <button
                        type="button"
                        onClick={() => handleCheckupChoice(false)}
                        className={`px-2 py-1.5 rounded-lg text-[12px] font-semibold active:scale-95 ${
                          checkupAccepted === false
                            ? "bg-gray-500 text-white"
                            : "bg-white border-2 border-amber-300 text-amber-900 hover:bg-amber-100"
                        }`}
                        style={{ touchAction: "manipulation", transition: "background-color 0.15s" }}
                        data-testid="ia-checkup-no"
                      >
                        Non merci
                      </button>
                      <button
                        type="button"
                        onClick={() => handleCheckupChoice(false)}
                        className="px-2 py-1.5 rounded-lg text-[12px] font-medium bg-white border-2 border-amber-200 text-amber-700 hover:bg-amber-50 active:scale-95"
                        style={{ touchAction: "manipulation", transition: "background-color 0.15s" }}
                        data-testid="ia-checkup-later"
                      >
                        Plus tard
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* iter129 — Section "Véhicule de remplacement ?" (entre service et calendrier) */}
          {!sending && !booked && selectedService && (() => {
            const cat = categories.find((c) => c.id === selectedService.id);
            if (cat?.subcategories?.length > 0 && !selectedSubcat) return null;
            if (cat?.propose_checkup_upsell && checkupConfig?.actif && checkupAccepted === null) return null;
            const isCarro = cat?.id === "carrosserie" || cat?.label?.toLowerCase().includes("carrosserie");
            return (
              <div className="space-y-2 pt-1" data-testid="ia-loaner-section">
                <div className="text-[12px] font-semibold text-gray-700 pl-1">Véhicule de remplacement ?</div>
                <div className="text-[11px] text-gray-600 leading-snug pl-1" data-testid="ia-loaner-info">
                  {isCarro ? (
                    <>Véhicule de remplacement <b>gratuit selon disponibilité</b>.</>
                  ) : (
                    <>Véhicule de remplacement <b>entre 40.– et 100.– par jour</b> selon disponibilité.</>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    disabled={busy8s}
                    onClick={() => {
                      if (busy8s) return;
                      triggerBusy8s(800);
                      // iter147 — Réarme une fenêtre "propre" de 1.5 s en annulant
                      // tout timer en cours + en remettant la signature à zéro.
                      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
                      setAutoSendPending(false);
                      lastAutoSentSignatureRef.current = null;
                      setWantsLoaner(true);
                      setSelectedFlotteId(null);
                      // Charge la liste des dates où au moins 1 véhicule est dispo pour griser le calendrier
                      const typeId = selectedSubcat?.subcategory?.type_intervention_id
                        || selectedService?.type_intervention_id || null;
                      const catLabel2 = (selectedService?.label || "").toLowerCase();
                      const svcCat = catLabel2.includes("carrosserie") ? "carrosserie" : null;
                      axios.get(`${BACKEND_URL}/api/public/ia-rdv/dates-flotte-dispo`, {
                        params: {
                          days: 60,
                          ...(typeId ? { type_intervention_id: typeId } : {}),
                          ...(svcCat ? { service_categorie: svcCat } : {}),
                        },
                      })
                        .then(({ data }) => setFlotteAvailableDates(new Set(data.available_dates || [])))
                        .catch(() => setFlotteAvailableDates(null));
                    }}
                    className={`px-3 py-2.5 rounded-xl text-[12.5px] font-semibold active:scale-95 ${
                      wantsLoaner === true
                        ? "bg-[#52a675] text-white"
                        : "bg-white border-2 border-[#52a675] text-[#2d5f3f] hover:bg-[#52a675]/5"
                    } ${busy8s ? "opacity-50 cursor-not-allowed" : ""}`}
                    style={{ touchAction: "manipulation", transition: "background-color 0.15s" }}
                    data-testid="ia-loaner-yes"
                  >
                    Oui
                  </button>
                  <button
                    type="button"
                    disabled={busy8s}
                    onClick={() => {
                      if (busy8s) return;
                      triggerBusy8s(800);
                      // iter147 — Réarme une fenêtre "propre" de 1.5 s.
                      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
                      setAutoSendPending(false);
                      lastAutoSentSignatureRef.current = null;
                      setWantsLoaner(false);
                      setSelectedFlotteId(null);
                      setFlotteAvailableDates(null);
                    }}
                    className={`px-3 py-2.5 rounded-xl text-[12.5px] font-semibold active:scale-95 ${
                      wantsLoaner === false
                        ? "bg-gray-600 text-white"
                        : "bg-white border-2 border-gray-300 text-gray-700 hover:bg-gray-50"
                    } ${busy8s ? "opacity-50 cursor-not-allowed" : ""}`}
                    style={{ touchAction: "manipulation", transition: "background-color 0.15s" }}
                    data-testid="ia-loaner-no"
                  >
                    Non
                  </button>
                </div>
              </div>
            );
          })()}

          {/* iter112 — Section 2 : Quand venir ? (info 9h/14h + première dispo + matin/après-midi + autre date) */}
          {!sending && !booked && slotSuggestions.length > 0 && wantsLoaner !== null && (
            <div className="space-y-2 pt-1" data-testid="ia-when-section">
              <div className="text-[12px] font-semibold text-gray-700 pl-1">2. Quand souhaitez-vous venir ?</div>
              <div className="text-[11px] text-gray-500 leading-snug pl-1" data-testid="ia-when-help">
                Sélectionnez votre créneau. <b>Matin</b> : véhicule à déposer <b>avant 09h00</b> (retour avant midi).
                {" "}<b>Après-midi</b> : véhicule à déposer <b>avant 14h00</b> (retour dans l&apos;après-midi).
                Vous pouvez aussi déposer la veille au soir, ou le matin pour l&apos;après-midi.
              </div>

              {/* Première disponibilité (carte mise en avant) — iter114bis : confirmation via "OK"
                  iter150 — Si wantsLoaner=true et flotteAvailableDates connu, on trouve la
                  1ère date qui passe TOUS les filtres : workshop ouvert + cutoff + véhicule de prêt.
                  Si aucune ne convient, on affiche un message clair au lieu d'un bouton qui mènerait à une impasse. */}
              {(() => {
                if (selectedDate) return null;
                const candidates = daysFromSlots.filter((d) => {
                  if (closedDates[d.date]) return false;
                  if (minBookableDate && d.date < minBookableDate) return false;
                  if (wantsLoaner === true && flotteAvailableDates && !flotteAvailableDates.has(d.date)) return false;
                  return true;
                });
                const first = candidates[0];
                if (!first) {
                  // Pas de 1ère dispo qui passe tous les filtres → message clair
                  const reason = wantsLoaner === true
                    ? "Aucune date disponible avec véhicule de remplacement sur les prochaines semaines."
                    : "Aucune date disponible sur les prochaines semaines.";
                  return (
                    <div className="w-full px-3.5 py-2.5 rounded-xl border-2 border-amber-300 bg-amber-50 text-[12.5px] text-amber-900" data-testid="ia-no-first-availability">
                      {reason}{" "}
                      {wantsLoaner === true && (
                        <button
                          type="button"
                          className="underline font-semibold text-amber-900"
                          onClick={() => {
                            if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
                            setAutoSendPending(false);
                            lastAutoSentSignatureRef.current = null;
                            setWantsLoaner(false);
                            setSelectedFlotteId(null);
                            setFlotteAvailableDates(null);
                          }}
                          data-testid="ia-switch-no-loaner"
                        >
                          Passer sans véhicule de remplacement
                        </button>
                      )}
                      {" "}ou bien{" "}
                      <button
                        type="button"
                        className="underline font-semibold text-amber-900"
                        onClick={handleOtherDate}
                        data-testid="ia-no-first-other-date"
                      >
                        choisir une autre date
                      </button>.
                    </div>
                  );
                }
                return (
                  <button
                    type="button"
                    onClick={() => handlePickDay(first.date)}
                    className="w-full flex items-center justify-between gap-2 px-3.5 py-2.5 rounded-xl border-2 border-[#52a675] bg-[#52a675]/5 hover:bg-[#52a675]/10 active:scale-[0.98]"
                    style={{ touchAction: "manipulation", transition: "background-color 0.15s" }}
                    data-testid="ia-first-availability"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-9 h-9 rounded-lg bg-[#52a675] flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-4.5 h-4.5 text-white" />
                      </div>
                      <div className="text-left min-w-0">
                        <div className="text-[10px] uppercase tracking-wide text-[#2d5f3f] font-semibold">
                          Première disponibilité{wantsLoaner === true ? " avec véhicule de prêt" : ""}
                        </div>
                        <div className="text-[14px] font-semibold text-[#2d5f3f] leading-tight truncate">{formatLongDay(first.date)}</div>
                      </div>
                    </div>
                    <span
                      className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#52a675] text-white text-[12px] font-bold shadow-sm"
                      data-testid="ia-first-availability-ok"
                    >
                      OK
                    </span>
                  </button>
                );
              })()}

              {/* Cards matin / après-midi — iter114bis : visibles UNIQUEMENT après confirmation date */}
              {(() => {
                const dayForCres = selectedDate; // plus de fallback automatique vers daysFromSlots[0]
                if (!dayForCres) return null;
                const cres = cresForDay(dayForCres);
                const hasMatin = cres.length === 0 || cres.some((c) => c.creneau === "matin");
                const hasApresMidi = cres.length === 0 || cres.some((c) => c.creneau === "apres_midi");
                const day = dayForCres;
                return (
                  <div ref={creneauRef} className="grid grid-cols-1 gap-2" data-testid="ia-creneau-suggestions">
                    {selectedDate && (
                      <div className="text-[11px] text-gray-500 pl-1">{formatLongDay(selectedDate)} :</div>
                    )}
                    {hasMatin && (
                      <button
                        type="button"
                        onClick={() => handlePickCreneau(day, "matin")}
                        className={`flex items-center justify-between px-3.5 py-2.5 rounded-xl border-2 active:scale-[0.98] ${
                          selectedCreneau === "matin" && selectedDate === day
                            ? "bg-[#52a675] border-[#52a675] text-white"
                            : "bg-white border-gray-200 text-gray-800 hover:border-[#52a675]"
                        }`}
                        style={{ touchAction: "manipulation", transition: "background-color 0.15s, border-color 0.15s" }}
                        data-testid="ia-creneau-matin"
                        aria-pressed={selectedCreneau === "matin" && selectedDate === day ? "true" : "false"}
                      >
                        <div className="text-left">
                          <div className="text-[14px] font-semibold leading-tight">Rendez-vous matin</div>
                          <div className={`text-[11px] leading-tight ${selectedCreneau === "matin" && selectedDate === day ? "text-white/85" : "text-gray-500"}`}>
                            Véhicule à déposer avant 09h00
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 flex-shrink-0" />
                      </button>
                    )}
                    {hasApresMidi && (
                      <button
                        type="button"
                        onClick={() => handlePickCreneau(day, "apres_midi")}
                        className={`flex items-center justify-between px-3.5 py-2.5 rounded-xl border-2 active:scale-[0.98] ${
                          selectedCreneau === "apres_midi" && selectedDate === day
                            ? "bg-[#52a675] border-[#52a675] text-white"
                            : "bg-white border-gray-200 text-gray-800 hover:border-[#52a675]"
                        }`}
                        style={{ touchAction: "manipulation", transition: "background-color 0.15s, border-color 0.15s" }}
                        data-testid="ia-creneau-apres-midi"
                        aria-pressed={selectedCreneau === "apres_midi" && selectedDate === day ? "true" : "false"}
                      >
                        <div className="text-left">
                          <div className="text-[14px] font-semibold leading-tight">Rendez-vous après-midi</div>
                          <div className={`text-[11px] leading-tight ${selectedCreneau === "apres_midi" && selectedDate === day ? "text-white/85" : "text-gray-500"}`}>
                            Véhicule à déposer avant 14h00
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 flex-shrink-0" />
                      </button>
                    )}
                    {selectedDate && (
                      <button
                        type="button"
                        onClick={() => { setSelectedDate(null); setSelectedCreneau(null); setSelectedFlotteId(null); }}
                        className="self-start px-3 py-1.5 rounded-lg bg-white border border-gray-300 text-gray-600 text-[12px] hover:border-gray-400 active:scale-95"
                        style={{ touchAction: "manipulation", transition: "border-color 0.15s" }}
                        data-testid="ia-creneau-back"
                      >
                        ← Autre jour
                      </button>
                    )}
                  </div>
                );
              })()}

              {/* iter129 — Picker véhicule de remplacement (après créneau, avant submit) */}
              {wantsLoaner === true && selectedDate && selectedCreneau && (
                <div className="space-y-2 pt-2 border-t border-gray-100 mt-2" data-testid="ia-loaner-picker">
                  <div className="text-[12px] font-semibold text-gray-700 pl-1">
                    Véhicule de remplacement {loanerLoading ? "…" : `(${loanerOptions.length} dispo)`}
                  </div>
                  {loanerLoading && (
                    <div className="text-[11px] text-gray-500 pl-1">Chargement des disponibilités…</div>
                  )}
                  {!loanerLoading && loanerOptions.length === 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-[12px] text-amber-900" data-testid="ia-loaner-aucune">
                      Aucun véhicule de remplacement disponible ce jour-là.{" "}
                      <button
                        type="button"
                        onClick={() => { setSelectedDate(null); setSelectedCreneau(null); setSelectedFlotteId(null); }}
                        className="underline font-semibold hover:no-underline"
                        data-testid="ia-loaner-choose-other-date"
                      >
                        Choisir une autre date
                      </button>
                    </div>
                  )}
                  {!loanerLoading && loanerOptions.length > 0 && (
                    <div className="grid grid-cols-1 gap-1.5">
                      {loanerOptions.map((v) => {
                        const isSel = selectedFlotteId === v.id;
                        return (
                          <button
                            key={v.id}
                            type="button"
                            disabled={busy8s}
                            onClick={() => { if (!busy8s) { triggerBusy8s(800); setSelectedFlotteId(v.id); } }}
                            className={`flex items-center justify-between px-3 py-2 rounded-lg border-2 text-left active:scale-[0.98] ${
                              isSel
                                ? "bg-[#52a675] border-[#52a675] text-white"
                                : "bg-white border-gray-200 hover:border-[#52a675]"
                            } ${busy8s && !isSel ? "opacity-50 cursor-not-allowed" : ""}`}
                            style={{ touchAction: "manipulation", transition: "background-color 0.15s, border-color 0.15s" }}
                            data-testid={`ia-loaner-veh-${v.id}`}
                          >
                            <div>
                              <div className="text-[13px] font-semibold leading-tight">
                                {v.marque} {v.modele}
                              </div>
                              <div className={`text-[10.5px] leading-tight ${isSel ? "text-white/85" : "text-gray-500"}`}>
                                {v.plaque}
                              </div>
                            </div>
                            <div className={`text-[11px] font-mono ${isSel ? "text-white" : "text-gray-700"}`}>
                              {v.courtoisie ? "Offert" : `${v.tarif_jour_effectif.toFixed(0)} CHF/j`}
                            </div>
                          </button>
                        );
                      })}
                      <div className="text-[10px] text-gray-400 pl-1 italic">
                        Prix indicatif — sera confirmé au comptoir.
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Bouton "Choisir une autre date" → ouvre mini-calendrier inline */}
              <button
                type="button"
                onClick={handleOtherDate}
                className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-white border border-gray-300 text-gray-700 text-[12px] font-medium hover:border-[#52a675] hover:text-[#2d5f3f] active:scale-95"
                style={{ touchAction: "manipulation", transition: "border-color 0.15s, color 0.15s" }}
                data-testid="ia-day-chip-other"
              >
                <Calendar className="w-3.5 h-3.5" />
                {showCalendar ? "Masquer le calendrier" : "Choisir une autre date"}
              </button>

              {/* iter112 — Mini-calendrier inline (jours fériés + week-ends grisés avec cadenas)
                  iter114bis — Wrapper + ref pour scroll-into-view automatique à l'ouverture. */}
              {showCalendar && (
                <div ref={calendarRef}>
                  <InlineCalendar
                    month={calMonth}
                    minBookableDate={minBookableDate}
                    closedDates={(() => {
                      // iter129 — Si wantsLoaner=true, fusionner les jours SANS flotte dispo dans closedDates
                      if (wantsLoaner !== true || !flotteAvailableDates) return closedDates;
                      const merged = { ...closedDates };
                      const start = new Date(calMonth.year, calMonth.month, 1);
                      for (let i = 0; i < 90; i++) {
                        const d = new Date(start);
                        d.setDate(start.getDate() + i);
                        const iso = d.toISOString().split("T")[0];
                        if (!flotteAvailableDates.has(iso) && !merged[iso]) {
                          merged[iso] = { reason: "Aucun véhicule de remplacement", is_loaner_full: true };
                        }
                      }
                      return merged;
                    })()}
                    selectedDate={selectedDate}
                    onPrev={handleCalPrev}
                    onNext={handleCalNext}
                    onPick={handleCalPickDay}
                  />
                </div>
              )}
            </div>
          )}

          {booked && (
            <div className="mx-auto max-w-md bg-emerald-50 border border-emerald-200 rounded-xl p-4 mt-2" data-testid="ia-booking-success">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                <div className="font-semibold text-emerald-900">RDV confirmé</div>
              </div>
              <div className="text-sm text-emerald-900 space-y-0.5">
                <div><b>Date :</b> {booked.date} ({booked.creneau === "matin" ? "matin" : "après-midi"})</div>
                <div><b>Type :</b> {booked.type}</div>
                <div><b>Au nom de :</b> {booked.nom}</div>
              </div>
              <div className="text-[11px] text-emerald-700 mt-2">
                Un SMS de confirmation a été envoyé. À bientôt !
              </div>
            </div>
          )}
          {bookError && !booked && (
            <div className="mx-auto max-w-md bg-amber-50 border border-amber-200 rounded-xl p-3 mt-2" data-testid="ia-booking-error">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <div className="text-sm text-amber-900">{bookError}</div>
              </div>
            </div>
          )}
        </div>

        {/* iter138 — Indicateur discret pendant le timer 1.5s (anti-effet « bug »).
            Affiché juste au-dessus de la zone de saisie, transparent et léger. */}
        {autoSendPending && !sending && !booked && (
          <div className="px-3 py-1.5 bg-[#f6f8f6] border-t border-gray-100 flex items-center justify-center gap-2 text-[11px] text-gray-500" data-testid="ia-auto-send-pending">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#52a675] animate-pulse"></span>
            <span>Envoi automatique…</span>
            <button
              onClick={cancelAutoSend}
              className="ml-2 text-gray-400 hover:text-gray-700 underline"
              data-testid="ia-auto-send-cancel"
              type="button"
            >
              annuler
            </button>
          </div>
        )}

        {/* ── Zone de saisie (architecture chatbot car-main.ch iter103) ─ */}
        <div
          className="bg-[#f6f8f6] border-t border-gray-200 shrink-0"
          style={{
            padding: "8px 8px",
            paddingLeft: "max(0.5rem, env(safe-area-inset-left))",
            paddingRight: "max(0.5rem, env(safe-area-inset-right))",
            paddingBottom: isEmbed ? "max(0.5rem, env(safe-area-inset-bottom))" : "calc(8px + env(safe-area-inset-bottom))",
          }}
          data-testid="ia-rdv-input-container"
        >
          <div className="flex gap-2 items-end">
            <textarea
              ref={inputRef}
              value={draft}
              onChange={handleDraftChange}
              onFocus={handleInputFocus}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder={booked ? "Conversation terminée" : "Écris ton message…"}
              disabled={sending || !!booked}
              rows={1}
              maxLength={1000}
              name="chat-message"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="sentences"
              spellCheck={false}
              enterKeyHint="send"
              inputMode="text"
              data-testid="ia-rdv-input"
              className="flex-1 min-w-0 resize-none rounded-3xl border border-gray-300 bg-white px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#52a675]/40 focus:border-[#52a675] max-h-28"
              style={{ fontSize: "16px", lineHeight: "1.35" }}
            />
            <button
              onClick={handleSend}
              disabled={sending || !draft.trim() || !!booked}
              className="shrink-0 w-10 h-10 rounded-full bg-[#52a675] hover:bg-[#3d8a5c] active:scale-95 disabled:opacity-50 disabled:active:scale-100 text-white flex items-center justify-center"
              style={{ touchAction: "manipulation", transition: "background-color 0.15s, opacity 0.15s" }}
              data-testid="ia-rdv-send"
              type="button"
              aria-label="Envoyer"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 ml-0.5" />}
            </button>
          </div>
          <div className="text-[10px] text-gray-400 mt-1 text-center leading-tight">
            Léa peut faire des erreurs · Urgence : <b>{PHONE_DISPLAY}</b>
          </div>
        </div>
      </div>
    </div>
  );
}
